import Link from 'next/link';
import { useTranslation } from 'react-i18next';

import { Icon } from '@tmh-website/components/icons/phosphor-extension';
import type { Site } from '@tmh-website/shared/cms';
import type { ButtonThemeColor } from '@tmh-website/ui/Button';

import * as Styled from './SocialLinks.styles';

export interface SocialLinksProps {
  socialLinks?: Site['socialLinks'];
  themeColor: ButtonThemeColor;
  iconOpacity?: number;
  align?: 'left' | 'center' | 'right';
  noAddressElement?: boolean;
  /**
   * Provide a breakpoint at which the social links should be centered.
   * E.g. theme.breakpoint.md.up
   */
  centeredAt?: string;
}

const SocialLinks = ({
  socialLinks,
  themeColor,
  iconOpacity,
  align,
  centeredAt,
  noAddressElement = false,
  ...props
}: SocialLinksProps) => {
  const { t } = useTranslation();

  if (!socialLinks || socialLinks.length === 0) {
    return null;
  }

  return (
    <Styled.Container as={noAddressElement ? 'div' : 'address'} {...props}>
      <Styled.Ul aria-label={t('global:navigation.social')} $align={align} $centeredAt={centeredAt}>
        {socialLinks.map(entry => {
          if (entry.icon && entry.link) {
            return (
              <li key={entry.link}>
                <Link key={entry.link} href={entry.link} passHref target='_blank' title={entry.type}>
                  <Styled.SocialButton
                    aria-label={t('global:navigation.goToSocialProfile', { platform: entry.type }) as string}
                    themeColor={themeColor}
                    $iconOpacity={iconOpacity}>
                    {<Icon icon={entry.icon} />}
                  </Styled.SocialButton>
                </Link>
              </li>
            );
          }
          return null;
        })}
      </Styled.Ul>
    </Styled.Container>
  );
};

export default SocialLinks;
